import React, { useEffect, useMemo, useState } from 'react';
import {
	Drawer,
	DrawerBody,
	DrawerCloseButton,
	DrawerContent,
	DrawerHeader,
	DrawerOverlay,
	Flex,
	Spinner,
	Text,
	useDisclosure,
} from '@chakra-ui/react';
import { ExplorerInput } from './explorerInput';
import { Filter, SearchSummary } from './searchSummary';
import { Results, Type } from './results';
import { ActionCloseIcon } from 'src/utils/icons/actionClose';
import { t } from 'i18next';
import { useSearchProvider } from '../../providers/Search/searchProvider';
import { ICustomStyle } from '../App/Wrapper/Wrapper';
import { scrollInvisible } from '../../utils/themer';

export const Explorer = ({ customStyle }: ICustomStyle) => {
	const [searchText, handleSearchText] = useState('');
	const [searchTextDelayed, handleSearchTextDelayed] = useState('');
	const [isSearching, handleIsSearching] = useState(false);
	const [selectedSearchType, setSelectedSearchType] = useState(Filter.ALL);
	const { isOpen, onOpen, onClose } = useDisclosure();

	const [timeOut, handleTimeOut] = useState<any>(null);

	const { isLoading, loadData, data } = useSearchProvider();

	const typeOptions = {
		[Filter.ALL]: () => (
			<>
				<Results
					type={Type.GAMES}
					data={{
						payload: data.games,
						isMore: false,
						isLoadingMore: false,
						getMore: () => null as unknown as void,
					}}
				/>
				<Results
					type={Type.USERS}
					data={{
						payload: data.users,
						isMore: false,
						isLoadingMore: false,
						getMore: () => null as unknown as void,
					}}
				/>
				<Results
					type={Type.TRANSACTIONS}
					data={{
						payload: data.transactions,
						isMore: false,
						isLoadingMore: false,
						getMore: () => null as unknown as void,
					}}
				/>
			</>
		),
		[Filter.USER]: () => (
			<Results
				type={Type.USERS}
				data={{
					payload: data.users,
					isMore: false,
					isLoadingMore: false,
					getMore: () => null as unknown as void,
				}}
			/>
		),
		[Filter.TRANSACTION]: () => (
			<Results
				type={Type.TRANSACTIONS}
				data={{
					payload: data.transactions,
					isMore: false,
					isLoadingMore: false,
					getMore: () => null as unknown as void,
				}}
			/>
		),
		[Filter.GAME]: () => (
			<Results
				type={Type.GAMES}
				data={{
					payload: data.games,
					isMore: false,
					isLoadingMore: false,
					getMore: () => null as unknown as void,
				}}
			/>
		),
	};

	const connectType = useMemo(() => {
		if (Filter.ALL === selectedSearchType) {
			return Filter.ALL;
		}
		if (Filter.GAME === selectedSearchType) {
			return Filter.GAME;
		}
		if (Filter.USER === selectedSearchType) {
			return Filter.USER;
		}
		if (Filter.TRANSACTION === selectedSearchType) {
			return Filter.TRANSACTION;
		}
		return Filter.ALL;
	}, [selectedSearchType]);

	const gameExplorerSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
		const text = event.target.value;

		handleIsSearching(true);
		handleSearchText(text);
		if (text.length > 2) {
			onOpen();
		}
		if (timeOut) {
			clearTimeout(timeOut);
		}
		handleTimeOut(
			setTimeout(() => {
				handleIsSearching(false);
				clearTimeout(timeOut);
				handleSearchTextDelayed(text);
			}, 150)
		);
	};

	useEffect(() => {
		loadData(searchTextDelayed);
	}, [searchTextDelayed]);

	useEffect(() => {
		if (!data.games.length && !data.users.length && !data.transactions.length) {
			return;
		}

		//onOpen();
	}, [data]);

	const isAnyResults = data.games.length || data.users.length || data.transactions.length;

	return (
		<>
			<ExplorerInput
				value={searchText}
				handleChangeInput={gameExplorerSearch}
				isLoading={isLoading}
				customStyle={customStyle}
				handleBringUpSearch={onOpen}
			/>
			<Drawer
				isOpen={isOpen}
				placement="right"
				onClose={onClose}
			>
				<DrawerOverlay />
				<DrawerContent
					bg="#2D313A"
					maxWidth="484px"
					display="flex"
					gridGap="36px"
				>
					<DrawerHeader
						bg="#21242B"
						display="flex"
						position="relative"
						paddingTop="64px"
						paddingX="36px"
						paddingBottom="0"
						flexDirection="column"
						gridGap="12px"
					>
						<DrawerCloseButton
							_focus={{
								boxShadow: 'none',
							}}
							cursor="pointer"
							position="absolute"
							top={{ base: '0', hashup: '59px' }}
							left={{ base: '5px', hashup: '-54px' }}
							bg="#21242B"
							borderRadius="4px 0 0 4px"
							width="54px"
							height="70px"
							_hover={{
								bg: '#21242B',
							}}
						>
							<ActionCloseIcon />
						</DrawerCloseButton>
						<ExplorerInput
							autoFocus
							isVariantCompact
							value={searchText}
							handleChangeInput={gameExplorerSearch}
							isLoading={isLoading}
						/>
						<SearchSummary
							selectedType={selectedSearchType}
							setSelectedType={setSelectedSearchType}
							count={{
								all: data.users.length + data.games.length + data.transactions.length,
								user: data.users.length,
								game: data.games.length,
								transaction: data.transactions.length,
							}}
						/>
					</DrawerHeader>
					<DrawerBody
						color="white"
						css={scrollInvisible}
						bg="#2D313A"
						marginTop="-2px"
						paddingX="36px"
						paddingBottom="36px"
						paddingTop="0"
					>
						{isLoading ? (
							<Flex
								w="100%"
								justifyContent="center"
							>
								<Spinner size="md" />
							</Flex>
						) : isAnyResults ? (
							<Flex
								flexDirection="column"
								gridGap="36px"
							>
								{typeOptions[connectType]()}
							</Flex>
						) : (
							<Flex
								w="100%"
								justifyContent="center"
								fontSize="16px"
								lineHeight="20px"
								color="#8D94A4"
								marginTop="28px"
							>
								<Text>{t('default.noResults').toString()}</Text>
							</Flex>
						)}
					</DrawerBody>
				</DrawerContent>
			</Drawer>
		</>
	);
};
